// Browser-safe: never imports ./server, so the Chainalysis key can't reach a client bundle.
import { tryCatch } from '@repo/utils'

import { SCREEN_ADDRESS_ROUTE, type ScreenedAddress, screenedAddressSchema } from '../index'

export interface ScreenAddressClientInit extends RequestInit {
  endpoint?: string // override only for the Next basePath edge case
}

/** Transport failure from the screening route; `retryable` marks a transient outage. */
export class ScreeningError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    options?: ErrorOptions
  ) {
    super(message, options)
    this.name = 'ScreeningError'
  }
}

/**
 * POSTs `{ address }` to the consumer's screening route and returns its verdict.
 * Throws on any transport failure (network, non-2xx, unreadable body) so the caller can
 * retry a transient outage rather than mistake it for a block; the caller stays fail-closed
 * once retries are exhausted. Only a network error or a 502/503/504 is retryable.
 */
export async function screenAddress(
  address: string,
  init?: ScreenAddressClientInit
): Promise<ScreenedAddress> {
  const { endpoint = SCREEN_ADDRESS_ROUTE, ...requestInit } = init ?? {}
  const { data: screeningResponse, error: requestError } = await tryCatch(
    fetch(endpoint, {
      ...requestInit,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(requestInit.headers ?? {}) },
      body: JSON.stringify({ address })
    })
  )
  if (requestError) {
    // A caller's own AbortSignal is a cancellation, not an outage (any reason, incl. timeout).
    const aborted = requestInit.signal?.aborted || requestError.name === 'AbortError'
    throw new ScreeningError('Address screening request failed', !aborted, { cause: requestError })
  }
  if (!screeningResponse.ok) {
    // 503 is the handler's transient signal; 502/504 are proxy outages in front of it.
    const { status } = screeningResponse
    const transient = status === 503 || status === 502 || status === 504
    throw new ScreeningError(`Address screening failed: ${status}`, transient)
  }
  // A body stream cut mid-read rejects with a TypeError (transient); malformed JSON is a
  // SyntaxError and stays non-retryable.
  const { data: body, error: bodyError } = await tryCatch<unknown>(screeningResponse.json())
  if (bodyError) {
    throw new ScreeningError('Address screening body unreadable', bodyError instanceof TypeError, {
      cause: bodyError
    })
  }
  const parsed = screenedAddressSchema.safeParse(body)
  if (!parsed.success) {
    throw new ScreeningError('Address screening returned an unexpected body', false, {
      cause: parsed.error
    })
  }
  return parsed.data
}
